import { supabase } from './supabase';
import { cloudinaryService } from './cloudinary.service';

export interface UserProfile {
  id: string;
  display_name: string | null;
  username: string | null;
  avatar_url: string | null;
  banner_url: string | null;
  bio: string | null;
  role: string | null;
  created_at: string | null;
  updated_at: string | null;
}

export interface UpdateProfileData {
  display_name?: string;
  bio?: string;
  avatar_url?: string;
  banner_url?: string;
}

export interface VoteHistory {
  id: string;
  poll_id: string;
  option_id: string;
  created_at: string;
  poll: {
    title: string;
    status: string;
  };
  option: {
    text: string | null;
    contestant?: {
      full_name: string;
      photo_url: string | null;
    } | null;
  };
}

export interface UserPost {
  id: string;
  content: string;
  created_at: string;
  updated_at: string;
  visibility: string;
  is_pinned: boolean;
  group_id: string | null;
  group?: {
    name: string;
  } | null;
  _count?: {
    reactions: number;
    comments: number;
  };
}

export interface UserComment {
  id: string;
  content: string;
  created_at: string;
  post_id: string;
  post: {
    content: string;
    user_id: string;
    user: {
      display_name: string | null;
      avatar_url: string | null;
    };
  };
}

export interface UserGroup {
  id: string;
  name: string;
  description: string | null;
  avatar_url: string | null;
  cover_url: string | null;
  privacy: string;
  role: 'admin' | 'moderator' | 'member';
  joined_at: string;
  _count?: {
    members: number;
  };
}

class ProfileService {
  /**
   * Validate URL is from Cloudinary
   * Security: Prevent XSS and malicious URLs
   */
  private isValidCloudinaryUrl(url: string): boolean {
    if (!url) return false;

    // Allow Cloudinary URLs
    const cloudinaryPattern = /^https:\/\/res\.cloudinary\.com\/[a-zA-Z0-9_-]+\/image\/upload\/.+/;

    // Block dangerous protocols
    const dangerousProtocols = /^(javascript|data|vbscript|file):/i;
    if (dangerousProtocols.test(url)) {
      return false;
    }

    return cloudinaryPattern.test(url);
  }
  /**
   * Get user profile by ID (public access)
   * Security: Email is never exposed, only public profile data
   */
  async getProfile(userId: string): Promise<UserProfile | null> {
    try {
      const { data, error } = await supabase
        .from('profiles')
        .select('*')
        .eq('id', userId)
        .single();

      if (error) throw error;
      return data;
    } catch (error) {
      return null;
    }
  }

  /**
   * Update user profile
   * Security:
   * - Only the authenticated user can update their own profile
   * - RLS policies enforce user can only update their own data
   * - Username uniqueness is validated
   * - Sanitize inputs to prevent XSS
   */
  async updateProfile(userId: string, updates: UpdateProfileData): Promise<UserProfile | null> {
    try {
      // Verify authenticated user is updating their own profile
      const { data: { user } } = await supabase.auth.getUser();
      if (!user || user.id !== userId) {
        throw new Error('Unauthorized: You can only update your own profile');
      }

      // Sanitize text inputs
      const sanitizedUpdates: UpdateProfileData = {};

      if (updates.display_name !== undefined) {
        sanitizedUpdates.display_name = updates.display_name.trim().substring(0, 50);
      }

      if (updates.bio !== undefined) {
        sanitizedUpdates.bio = updates.bio.trim().substring(0, 500);
      }

      if (updates.avatar_url !== undefined) {
        // Validate avatar URL is from Cloudinary
        if (updates.avatar_url && !this.isValidCloudinaryUrl(updates.avatar_url)) {
          throw new Error('Invalid avatar URL. Only Cloudinary URLs are allowed.');
        }
        sanitizedUpdates.avatar_url = updates.avatar_url;
      }

      if (updates.banner_url !== undefined) {
        // Validate banner URL is from Cloudinary
        if (updates.banner_url && !this.isValidCloudinaryUrl(updates.banner_url)) {
          throw new Error('Invalid banner URL. Only Cloudinary URLs are allowed.');
        }
        sanitizedUpdates.banner_url = updates.banner_url;
      }

      // Update profile
      const { data, error } = await supabase
        .from('profiles')
        .update({
          ...sanitizedUpdates,
          updated_at: new Date().toISOString(),
        })
        .eq('id', userId)
        .select()
        .single();

      if (error) throw error;
      return data;
    } catch (error: any) {

      // Sanitize error messages before throwing to client
      if (error.message) {
        throw new Error(error.message);
      }
      throw new Error('Failed to update profile');
    }
  }

  /**
   * Upload avatar image
   * Security:
   * - File type validation (images only)
   * - File size limit (5MB)
   * - Only authenticated users can upload
   */
  async uploadAvatar(file: File, userId: string): Promise<string> {
    try {
      // Verify authentication
      const { data: { user } } = await supabase.auth.getUser();
      if (!user || user.id !== userId) {
        throw new Error('Unauthorized');
      }

      // Validate file type
      const validTypes = ['image/jpeg', 'image/png', 'image/jpg', 'image/webp'];
      if (!validTypes.includes(file.type)) {
        throw new Error('Invalid file type. Only JPG, PNG, and WebP images are allowed');
      }

      // Validate file size (5MB max)
      const maxSize = 5 * 1024 * 1024; // 5MB
      if (file.size > maxSize) {
        throw new Error('File size exceeds 5MB limit');
      }

      // Upload to Cloudinary
      const result = await cloudinaryService.uploadImage(file, 'lagranjavota/avatars');

      return result.secure_url;
    } catch (error: any) {
      throw new Error(error.message || 'Failed to upload avatar');
    }
  }

  /**
   * Upload banner image
   * Security: Same as uploadAvatar
   */
  async uploadBanner(file: File, userId: string): Promise<string> {
    try {
      // Verify authentication
      const { data: { user } } = await supabase.auth.getUser();
      if (!user || user.id !== userId) {
        throw new Error('Unauthorized');
      }

      // Validate file type
      const validTypes = ['image/jpeg', 'image/png', 'image/jpg', 'image/webp'];
      if (!validTypes.includes(file.type)) {
        throw new Error('Invalid file type. Only JPG, PNG, and WebP images are allowed');
      }

      // Validate file size (5MB max)
      const maxSize = 5 * 1024 * 1024; // 5MB
      if (file.size > maxSize) {
        throw new Error('File size exceeds 5MB limit');
      }

      // Upload to Cloudinary
      const result = await cloudinaryService.uploadImage(file, 'lagranjavota/banners');

      return result.secure_url;
    } catch (error: any) {
      throw new Error(error.message || 'Failed to upload banner');
    }
  }

  /**
   * Get user voting history
   * Security: Users can only see their own voting history unless it's public
   */
  async getUserVotingHistory(userId: string, limit: number = 50): Promise<VoteHistory[]> {
    try {
      // Check if requesting user is viewing their own profile
      const { data: { user } } = await supabase.auth.getUser();
      const isOwnProfile = user?.id === userId;

      // Only allow viewing own voting history for privacy
      if (!isOwnProfile) {
        throw new Error('Voting history is private');
      }

      const { data, error } = await supabase
        .from('votes')
        .select(`
          id,
          poll_id,
          option_id,
          created_at,
          points,
          polls:poll_id (
            title,
            status
          ),
          poll_options:option_id (
            text,
            contestants:contestant_id (
              full_name,
              photo_url
            )
          )
        `)
        .eq('user_id', userId)
        .order('created_at', { ascending: false })
        .limit(limit);

      if (error) throw error;

      // Transform data
      return (data || []).map((vote: any) => ({
        id: vote.id,
        poll_id: vote.poll_id,
        option_id: vote.option_id,
        created_at: vote.created_at,
        poll: {
          title: vote.polls?.title || 'Encuesta eliminada',
          status: vote.polls?.status || 'unknown',
        },
        option: {
          text: vote.poll_options?.text,
          contestant: vote.poll_options?.contestants || null,
        },
      }));
    } catch (error: any) {
      throw new Error(error.message || 'Failed to fetch voting history');
    }
  }

  /**
   * Get user posts
   * Security: Respects post visibility settings
   */
  async getUserPosts(userId: string, page: number = 1, limit: number = 20): Promise<UserPost[]> {
    try {
      const { data: { user } } = await supabase.auth.getUser();
      const isOwnProfile = user?.id === userId;
      const offset = (page - 1) * limit;

      let query = supabase
        .from('posts')
        .select(`
          id,
          content,
          created_at,
          updated_at,
          visibility,
          is_pinned,
          group_id,
          groups:group_id (
            name
          )
        `)
        .eq('user_id', userId)
        .eq('is_hidden', false);

      // Filter by visibility
      if (!isOwnProfile) {
        query = query.eq('visibility', 'public');
      }

      const { data, error } = await query
        .order('created_at', { ascending: false })
        .range(offset, offset + limit - 1);

      if (error) throw error;

      return (data || []).map((post: any) => ({
        id: post.id,
        content: post.content,
        created_at: post.created_at,
        updated_at: post.updated_at,
        visibility: post.visibility,
        is_pinned: post.is_pinned,
        group_id: post.group_id,
        group: post.groups || null,
      }));
    } catch (error: any) {
      throw new Error(error.message || 'Failed to fetch user posts');
    }
  }

  /**
   * Get user comments
   * Security: Only show comments on visible posts
   */
  async getUserComments(userId: string, page: number = 1, limit: number = 20): Promise<UserComment[]> {
    try {
      const offset = (page - 1) * limit;

      const { data, error } = await supabase
        .from('comments')
        .select(`
          id,
          content,
          created_at,
          post_id,
          posts:post_id (
            content,
            author_id,
            profiles:author_id (
              display_name,
              avatar_url
            )
          )
        `)
        .eq('author_id', userId)
        .order('created_at', { ascending: false })
        .range(offset, offset + limit - 1);

      if (error) throw error;

      return (data || []).map((comment: any) => ({
        id: comment.id,
        content: comment.content,
        created_at: comment.created_at,
        post_id: comment.post_id,
        post: {
          content: comment.posts?.content || '',
          user_id: comment.posts?.author_id || '',
          user: comment.posts?.profiles || {
            display_name: null,
            avatar_url: null,
          },
        },
      }));
    } catch (error: any) {
      throw new Error(error.message || 'Failed to fetch user comments');
    }
  }

  /**
   * Get user groups
   * Security: Only show public groups or groups user is member of
   */
  async getUserGroups(userId: string): Promise<UserGroup[]> {
    try {
      const { data: { user } } = await supabase.auth.getUser();
      const isOwnProfile = user?.id === userId;

      const { data, error } = await supabase
        .from('group_members')
        .select(`
          role,
          joined_at,
          groups:group_id (
            id,
            name,
            description,
            avatar_url,
            cover_url,
            privacy
          )
        `)
        .eq('user_id', userId);

      if (error) throw error;

      // Filter by privacy if not own profile
      let groups = data || [];
      if (!isOwnProfile) {
        groups = groups.filter((g: any) => g.groups?.privacy === 'public');
      }

      return groups.map((membership: any) => ({
        id: membership.groups.id,
        name: membership.groups.name,
        description: membership.groups.description,
        avatar_url: membership.groups.avatar_url,
        cover_url: membership.groups.cover_url,
        privacy: membership.groups.privacy,
        role: membership.role,
        joined_at: membership.joined_at,
      }));
    } catch (error: any) {
      throw new Error(error.message || 'Failed to fetch user groups');
    }
  }

  /**
   * Delete user account
   * Security:
   * - Only authenticated user can delete their own account
   * - Soft delete by default (mark as suspended and anonymize data)
   * - Hard delete requires admin privileges
   */
  async deleteAccount(userId: string, hardDelete: boolean = false): Promise<void> {
    try {
      // Verify authentication
      const { data: { user } } = await supabase.auth.getUser();
      if (!user || user.id !== userId) {
        throw new Error('Unauthorized: You can only delete your own account');
      }

      if (hardDelete) {
        // Hard delete - requires admin (would be implemented server-side for security)
        throw new Error('Hard delete must be requested through support');
      } else {
        // Soft delete - anonymize profile
        const { error } = await supabase
          .from('profiles')
          .update({
            display_name: 'Usuario eliminado',
            avatar_url: null,
            banner_url: null,
            bio: null,
            updated_at: new Date().toISOString(),
          })
          .eq('id', userId);

        if (error) throw error;

        // Sign out user
        await supabase.auth.signOut();
      }
    } catch (error: any) {
      throw new Error(error.message || 'Failed to delete account');
    }
  }

  /**
   * Get profile statistics
   */
  async getProfileStats(userId: string): Promise<{
    totalVotes: number;
    totalPosts: number;
    totalComments: number;
    memberSince: string | null;
  }> {
    try {
      const profile = await this.getProfile(userId);

      // Get vote count
      const { count: voteCount } = await supabase
        .from('votes')
        .select('*', { count: 'exact', head: true })
        .eq('user_id', userId);

      // Get post count
      const { count: postCount } = await supabase
        .from('posts')
        .select('*', { count: 'exact', head: true })
        .eq('user_id', userId)
        .eq('is_hidden', false);

      // Get comment count
      const { count: commentCount } = await supabase
        .from('comments')
        .select('*', { count: 'exact', head: true })
        .eq('user_id', userId)
        .eq('is_hidden', false);

      return {
        totalVotes: voteCount || 0,
        totalPosts: postCount || 0,
        totalComments: commentCount || 0,
        memberSince: profile?.created_at || null,
      };
    } catch (error) {
      return {
        totalVotes: 0,
        totalPosts: 0,
        totalComments: 0,
        memberSince: null,
      };
    }
  }
}

export const profileService = new ProfileService();
